<template>
  <MainLayout>
    <ContentPageHeader functionName="買取査定管理" pageName="査定登録">
    </ContentPageHeader>
    
    <div class="assessment-register-form">
      <div class="form-section">
        <!-- 買取日 -->
        <FormDateField 
          label="買取日"
          id="purchaseDate" 
          v-model="purchase.purchaseDate"
          required
        />

        <!-- 買取店舗 -->
        <FormSelectBoxField 
          label="買取店舗" 
          id="purchaseStore" 
          v-model="purchase.purchaseStore"
          :options="storeOptions"
          required
          size="small"
        />

        <!-- 担当者 -->
        <FormSelectTextField 
          label="担当者" 
          id="assessmentStaff" 
          v-model="purchase.assessmentStaff"
          :options="staffOptions"
          required
          :maxlength="100"
        />

        <!-- 備考 -->
        <FormTextareaField 
          label="備考" 
          id="remarks" 
          v-model="purchase.remarks" 
          :rows="8"
          :maxlength="100"
        />
      </div>
    </div>
    
    <template #footer-buttons>
      <div class="footer-buttons">
        <button class="btn btn-primary" @click="registerPurchase">登録</button>
      </div>
    </template>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormSelectBoxField from '@/components/form/FormSelectBoxField.vue'
import FormSelectTextField from '@/components/form/FormSelectTextField.vue'
import FormDateField from '@/components/form/FormDateField.vue'
import FormTextareaField from '@/components/form/FormTextareaField.vue'
import apiService from '@/services/service.js'
import { showLoading, hideLoading } from '@/utils/loadingEventBus';
import constants from '../../constants/constants.json';

export default {
  name: 'PurchaseRegisterView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormSelectBoxField,
    FormSelectTextField,
    FormDateField,
    FormTextareaField,
  },
  data() {
    return {
      purchase: [],
      storeOptions: [], // 初期値を空配列に変更
      staffOptions: this.getStaffList() // ローカルストレージから取得
    };
  },
  computed: {
    // ユーザー権限の取得
    userAuthority() {
      return this.$store.getters['auth/currentUser']?.authority || constants.Authority.STAFF.id;
    },
    
    // ユーザーの所属店舗ID
    userStoreId() {
      return this.$store.getters['auth/currentUser']?.storeId || null;
    },
    
    defaultPurchase() {
      // ユーザーの店舗IDを初期値に設定
      return {
        purchaseDate: this.getTodayDate(), // 本日日付をデフォルト値に設定
        purchaseStore: this.userStoreId ? this.userStoreId : '',
        assessmentStaff: '',
        remarks: '',
        customerName: '',
        customerNameKana: '',
        contactNumber: '',
        birthDate: '',
        postalCode: '',
        address: '',
        qualifiedInvoiceIssuer: '' // 適格請求書発行事業者
      };
    },
  },
  methods: {
    // 本日日付を取得する（YYYY-MM-DD形式）
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    async registerPurchase() {
      try {
        showLoading();
        // Id指定がある場合は
        const isUpdate = this.$route.params.id ? true : false;

        // バリデーションチェック
        if (!this.purchase.purchaseDate) {
          alert('買取日は必須です。');
          return;
        }
        if (!this.purchase.purchaseStore) {
          alert('買取店舗は必須です。');
          return;
        }
        if (!this.purchase.assessmentStaff) {
          alert('担当者は必須です。');
          return;
        }

        // 桁数チェック
        if (this.purchase.assessmentStaff && this.purchase.assessmentStaff.length > 100) {
          alert('担当者は100文字以内で入力してください。');
          return;
        }
        if (this.purchase.remarks && this.purchase.remarks.length > 1000) {
          alert('備考は1000文字以内で入力してください。');
          return;
        }
        
        // 権限チェック
        if (this.userAuthority !== constants.Authority.ADMIN.id) {
          // 店長またはスタッフは所属店舗のみ選択可能
          if (this.purchase.purchaseStore != this.userStoreId) {
            alert('所属店舗以外は選択できません。');
            return;
          }
        }

        // 登録確認ダイアログ
        if (!confirm(isUpdate 
          ? '買取査定情報を更新します。\nよろしいですか？' 
          : '買取査定情報を登録します。\nよろしいですか？')) return;

        // PurchaseRequest形式に変換
        const purchaseRequest = {
          commonPurchaseSlip: {
            id: isUpdate ? this.$route.params.id : null, // 新規登録時はnull
            purchaseDate: new Date(this.purchase.purchaseDate).toISOString(),
            storeId: this.purchase.purchaseStore,
            staffName: this.purchase.assessmentStaff,
            customerName: this.purchase.customerName,
            customerNameKana: this.purchase.customerNameKana,
            contactAddress: this.purchase.contactNumber,
            birthdate: this.purchase.birthDate ? new Date(this.purchase.birthDate).toISOString() : '',
            postalCode: this.purchase.postalCode,
            address: this.purchase.address,
            isBusinessOperator: this.purchase.qualifiedInvoiceIssuer === 'yes',
            note: this.purchase.remarks
          },
          purchaseSlips: [], // 必要に応じてデータを追加
          smallArticlePurchaseSlips: [] // 必要に応じてデータを追加
        };

        // 入力情報をDBに保存
        const response = isUpdate
          ? await apiService.updatePurchase(purchaseRequest)
          : await apiService.registerPurchase(purchaseRequest);
          
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);

        this.saveStaffList(); // スタッフ情報をローカルストレージに保存
        sessionStorage.setItem('legitimateNavigation', 'PurchaseDataRegister');
        this.$router.push({ name: 'PurchaseDataRegister', params: { id: response.data.commonPurchaseSlip.id } });

      } catch (error) {
        alert('買取査定情報の登録中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    async loadStoreOptions() {
      try {
        showLoading();
        const response = await apiService.getStoreList();
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);
        
        this.storeOptions = response.data.stores.map(store => ({
          value: store.id,
          label: store.storeName
        }));
      } catch (error) {
        console.error('店舗情報の取得に失敗しました:', error);
        alert('店舗情報の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    async loadPurchase(purchaseSlipId) {
      try {
        showLoading();
        const response = await apiService.getPurchaseById(purchaseSlipId);
        if (!response || response.status !== 200) throw new Error('error status: ' + response.status);
        if (!(response && response.data)) return;
        const commonPurchaseSlip = response.data.commonPurchaseSlip;
        this.purchase = {
          purchaseDate: commonPurchaseSlip.purchaseDate ? commonPurchaseSlip.purchaseDate.split('T')[0] : '',
          purchaseStore: commonPurchaseSlip.storeId || '',
          assessmentStaff: commonPurchaseSlip.staffName || '',
          remarks: commonPurchaseSlip.note || '',
          customerName: commonPurchaseSlip.customerName || '',
          customerNameKana: commonPurchaseSlip.customerNameKana || '',
          contactNumber: commonPurchaseSlip.contactAddress || '',
          birthDate: commonPurchaseSlip.birthdate ? new Date(commonPurchaseSlip.birthdate).toISOString().split('T')[0] : '',
          postalCode: commonPurchaseSlip.postalCode || '',
          address: commonPurchaseSlip.address || '',
          qualifiedInvoiceIssuer: commonPurchaseSlip.isBusinessOperator ? 'yes' : 'no'
        };
      } catch (error) {
        console.error('買取査定情報の取得に失敗しました:', error);
        alert('買取査定情報の取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    getStaffList() {
      // ローカルストレージからstaffListを取得
      const storedOptions = localStorage.getItem('staffOptions');
      return storedOptions ? JSON.parse(storedOptions) : [];
    },
    saveStaffList() {
      // 現在のstaffListをローカルストレージに保存
      const newStaff = this.purchase.assessmentStaff;
      if (newStaff && !this.staffOptions.some(option => option.value === newStaff)) {
        this.staffOptions.unshift({ value: newStaff, label: newStaff });
        localStorage.setItem('staffOptions', JSON.stringify(this.staffOptions));
      }
    }
  },
  async mounted() {
    this.loadStoreOptions();
    if (this.$route.params.id) {
      this.loadPurchase(this.$route.params.id);
      return;
    }

    // 新規登録時の初期値設定
    this.purchase = this.defaultPurchase;
  }
}
</script>

<style scoped>
.assessment-register-form {
  display: flex;
  flex-direction: column;
  background-color: var(--gray-light);
}

.form-section {
  background-color: var(--gray-light);
  padding: 0;
}

.customer-info {
  margin-top: 1px;
  padding: 5px;
  border: 1px solid var(--gray-dark);
  border-radius: 4px;
}

.footer-buttons {
  display: flex; /* 必須: flexbox を有効化 */
  justify-content: flex-end; /* 子要素を右詰めに配置 */
  width: 100%; /* 必要に応じて幅を指定 */
  padding: 10px 0; /* 上下の余白を追加 */
}
</style>
